import { useEffect, useMemo, useState } from "react";
import { api, AUD, PCT } from "../lib/api";
import { ErrorCard, parseApiError } from "../components/ErrorCard";
import { toast } from "sonner";
import { PencilSimple, Check, X } from "@phosphor-icons/react";

const chip = (s) => s === "On Plan" ? "status-on-plan" : s === "Watch" ? "status-watch" : "status-review";

const VIEW_OPTIONS = [
    { key: "combined", label: "Combined", testid: "lifestyle-view-combined" },
    { key: "workbook", label: "Baseline", testid: "lifestyle-view-workbook" },
    { key: "transactions", label: "Transactions", testid: "lifestyle-view-transactions" },
];

function subMonths(ym, count) {
    let [y, m] = ym.split("-").map(Number);
    for (let i = 0; i < count; i++) { m -= 1; if (m < 1) { m = 12; y -= 1; } }
    return `${y}-${String(m).padStart(2, "0")}`;
}

function fyForMonth(ym) {
    // Return the Aus FY (starting year) that contains a `YYYY-MM` month.
    // Jul-Dec belong to FY starting that year; Jan-Jun belong to FY starting prior year.
    const [y, m] = ym.split("-").map(Number);
    return m >= 7 ? y : y - 1;
}
function fyLabel(startYear) {
    // e.g. 2025 → "FY 26" (short form: Australian FY ends the NEXT year)
    return `FY ${String((startYear + 1) % 100).padStart(2, "0")}`;
}
function fyRange(startYear) {
    // Full Jul-Jun window regardless of whether every month is in available list.
    const endYear = startYear + 1;
    return { from: `${startYear}-07`, to: `${endYear}-06` };
}

function actualFor(cat, view) {
    const wb = Number(cat.actual_workbook || 0);
    const tx = Number(cat.actual_from_transactions || 0);
    if (view === "workbook") return wb;
    if (view === "transactions") return tx;
    return Math.max(wb, tx);
}
function statusFor(actual, annual) {
    if (!annual) return "On Plan";
    const r = actual / annual;
    if (r <= 1.0) return "On Plan";
    if (r <= 1.2) return "Watch";
    return "Review";
}

function currentFY() {
    // Australian FY = Jul-Jun. Returns {from: "YYYY-07", to: "YYYY-06"} for the FY
    // that CONTAINS today (or FY26 = Jul25-Jun26 as a hard fallback).
    const now = new Date();
    const y = now.getUTCFullYear();
    const m = now.getUTCMonth() + 1;   // 1..12
    const startYear = m >= 7 ? y : y - 1;
    const endYear = startYear + 1;
    return {
        from: `${startYear}-07`,
        to: `${endYear.toString().padStart(4, "0")}-06`,
    };
}

export default function Lifestyle() {
    const [cats, setCats] = useState([]);
    const [view, setView] = useState("combined");
    const [available, setAvailable] = useState({ months: [], labels: {} });
    const [range, setRange] = useState({ from: null, to: null, active: false });
    const [editing, setEditing] = useState(null);   // { id, monthly, annual }
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [includeReview, setIncludeReview] = useState(false);
    const [pendingReviewCount, setPendingReviewCount] = useState(0);
    const [showWorkbook, setShowWorkbook] = useState(false);
    const [activeChip, setActiveChip] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerAnchor, setPickerAnchor] = useState(null); // ym of first click awaiting a 2nd

    // Load available months once. Pick default range by auto-detecting the
    // FY that has the most transactions (so returning users land on their
    // real data, not an empty future FY).
    useEffect(() => {
        api.get("/cashflow/available-months").then(r => {
            setAvailable(r.data);
            const m = r.data.months || [];
            if (!m.length) return;
            // Bucket months by FY start year and pick the FY with the most entries.
            const buckets = {};
            for (const ym of m) {
                const fy = fyForMonth(ym);
                buckets[fy] = (buckets[fy] || 0) + 1;
            }
            const bestFy = Object.entries(buckets)
                .sort((a, b) => b[1] - a[1])[0]?.[0];
            if (bestFy != null) {
                const rng = fyRange(Number(bestFy));
                setRange({ ...rng, active: true });
                setActiveChip(`fy-${bestFy}`);
            }
        }).catch(e => setError(parseApiError(e)));
    }, []);

    // Compute FY chip list from available months (fallback to current 3 FYs).
    const fyChips = useMemo(() => {
        const now = new Date();
        const nowFy = (now.getUTCMonth() + 1) >= 7
            ? now.getUTCFullYear()
            : now.getUTCFullYear() - 1;
        const set = new Set([nowFy, nowFy - 1, nowFy - 2]);
        for (const ym of (available.months || [])) set.add(fyForMonth(ym));
        return Array.from(set).sort((a, b) => b - a).slice(0, 5);
    }, [available.months]);

    // Load lifestyle whenever range or include-review toggles
    useEffect(() => {
        const params = new URLSearchParams();
        if (range.active && range.from && range.to) {
            params.set("from_ym", range.from);
            params.set("to_ym", range.to);
        }
        if (includeReview) params.set("include_review", "true");
        const qs = params.toString();
        const url = qs ? `/lifestyle?${qs}` : "/lifestyle";
        setError(null);
        api.get(url).then(r => {
            setCats(r.data);
            const hdr = r.headers?.["x-pending-review-count"];
            setPendingReviewCount(hdr ? Number(hdr) : 0);
        }).catch(e => setError(parseApiError(e)));
    }, [range, includeReview]);

    const rows = useMemo(() => cats.map(c => {
        const actual = actualFor(c, view);
        return { ...c, _actual: actual, _status: statusFor(actual, c.annual_budget) };
    }), [cats, view]);

    const totalBudget = rows.reduce((a, c) => a + Number(c.annual_budget || 0), 0);
    const totalActual = rows.reduce((a, c) => a + c._actual, 0);

    const setPreset = (kind) => {
        setActiveChip(kind);
        setPickerAnchor(null);
        const m = available.months || [];
        const to = m.length ? m[m.length - 1] : null;
        if (kind === "all") return setRange(r => ({ ...r, active: false }));
        if (kind.startsWith("fy-")) {
            const startYear = Number(kind.slice(3));
            return setRange({ ...fyRange(startYear), active: true });
        }
        if (!to) return;
        if (kind === "current") setRange({ from: to, to, active: true });
        else if (kind === "3mo") setRange({ from: subMonths(to, 2), to, active: true });
        else if (kind === "6mo") setRange({ from: subMonths(to, 5), to, active: true });
        else if (kind === "12mo") setRange({ from: subMonths(to, 11), to, active: true });
    };

    // Custom month picker: click a month once to select just that month;
    // click a SECOND month to extend the range to both. Auto-swap so
    // order-of-clicks doesn't matter. Any subsequent click resets to a new
    // single-month selection.
    const pickMonth = (ym) => {
        setActiveChip("custom");
        if (!pickerAnchor) {
            // First click — single month
            setRange({ from: ym, to: ym, active: true });
            setPickerAnchor(ym);
            return;
        }
        // Second click — range spanning anchor and this click
        const [from, to] = ym < pickerAnchor ? [ym, pickerAnchor] : [pickerAnchor, ym];
        setRange({ from, to, active: true });
        setPickerAnchor(null);
    };

    const inSelectedRange = (ym) =>
        range.active && range.from && range.to &&
        ym >= range.from && ym <= range.to;

    const startEdit = (c) => setEditing({
        id: c.id,
        monthly: Number(c.monthly_budget || 0),
        annual: Number(c.annual_budget || 0),
    });
    const cancelEdit = () => setEditing(null);

    // When user changes monthly, keep annual in sync unless they already edited it manually.
    const onMonthlyChange = (v) => {
        setEditing(e => ({ ...e, monthly: Number(v), annual: Number(v) * 12 }));
    };
    const onAnnualChange = (v) => {
        setEditing(e => ({ ...e, annual: Number(v) }));
    };

    const saveEdit = async (c) => {
        setBusy(true);
        try {
            await api.put(`/lifestyle/${c.id}`, {
                category: c.category,
                monthly_budget: editing.monthly,
                annual_budget: editing.annual,
                actual_fy26: c.actual_workbook || 0,
                notes: c.notes || "",
            });
            toast.success(`${c.category} budget updated`);
            setEditing(null);
            // Refresh from server so status recomputes
            const url = range.active && range.from && range.to
                ? `/lifestyle?from_ym=${range.from}&to_ym=${range.to}`
                : "/lifestyle";
            const { data } = await api.get(url);
            setCats(data);
        } catch {
            toast.error("Failed to update budget");
        } finally {
            setBusy(false);
        }
    };

    const resetWorkbookActuals = async () => {
        if (!window.confirm(
            "Zero the historical baseline actuals on every category?\n\n" +
            "After this, Lifestyle & Dashboard will track your live transactions purely " +
            "(no baseline floor). This can't be undone from the UI — you'd need to " +
            "re-enter the baseline values manually."
        )) return;
        setBusy(true);
        try {
            const { data } = await api.post("/lifestyle/reset-workbook-actuals");
            toast.success(`Cleared baseline floor on ${data.reset} categories — Lifestyle now tracks transactions purely`);
            const url = range.active && range.from && range.to
                ? `/lifestyle?from_ym=${range.from}&to_ym=${range.to}`
                : "/lifestyle";
            const { data: cats2 } = await api.get(url);
            setCats(cats2);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reset failed");
        } finally {
            setBusy(false);
        }
    };

    if (error) return <ErrorCard error={error} subject="lifestyle" testId="lifestyle-error" />;

    return (
        <div data-testid="lifestyle-page">
            <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Lifestyle Cost of Living</div>
                    <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">What our lifestyle costs.</h1>
                </div>
                <div className="hairline bg-white p-1 rounded inline-flex text-xs gap-2 items-center"
                     data-testid="lifestyle-view-toggle">
                    {VIEW_OPTIONS.map(v => (
                        <button key={v.key} onClick={() => setView(v.key)} data-testid={v.testid}
                                className={`px-3 py-2 rounded transition-colors ${
                                    view === v.key
                                        ? "bg-[var(--eucalyptus)] text-[var(--bone)]"
                                        : "text-[var(--ink)] hover:bg-black/5"
                                }`}>
                            {v.label}
                        </button>
                    ))}
                    <button onClick={resetWorkbookActuals} disabled={busy}
                            data-testid="lifestyle-reset-workbook-btn"
                            className="px-3 py-2 rounded text-[var(--muted)] hover:text-[var(--ink)] hover:bg-black/5 border-l border-[var(--line)]"
                            title="Zero the historical baseline actuals so Lifestyle tracks live transactions purely">
                        Reset baseline actuals
                    </button>
                </div>
            </div>

            {/* Date range */}
            {pendingReviewCount > 0 && !includeReview && (
                <div className="hairline p-4 rounded mb-4 flex items-center justify-between gap-4 flex-wrap"
                     style={{ background: "#fff7ed", borderColor: "#fdba74" }}
                     data-testid="lifestyle-pending-review-banner">
                    <div className="text-sm" style={{ color: "#7c2d12" }}>
                        <strong>{pendingReviewCount}</strong> transaction{pendingReviewCount === 1 ? "" : "s"} still marked "Needs Review" — not counted in Lifestyle actuals yet.
                        Approve them in <a href="/transactions" className="underline">Transactions → AI Review</a>, or tick "Include unreviewed" on the right to see totals-as-of-now.
                    </div>
                    <button onClick={() => setIncludeReview(true)}
                            className="btn-primary text-xs whitespace-nowrap"
                            data-testid="lifestyle-include-now-btn">
                        Include unreviewed now
                    </button>
                </div>
            )}

            {/* Date range — chip-based, no more awkward "Filter by month" checkbox. */}
            <div className="hairline bg-white p-5 rounded mb-8"
                 data-testid="lifestyle-range-controls">
                <div className="flex justify-between items-center mb-3 flex-wrap gap-3">
                    <div>
                        <div className="label-eyebrow mb-1">Range</div>
                        <div className="text-sm">
                            {range.active && range.from && range.to ? (
                                <>
                                    Showing <strong>{available.labels[range.from] || range.from}</strong>
                                    {" → "}
                                    <strong>{available.labels[range.to] || range.to}</strong>
                                </>
                            ) : (
                                <em style={{ color: "var(--muted)" }}>All time (every transaction ever)</em>
                            )}
                        </div>
                    </div>
                    <label className="text-xs flex items-center gap-2 cursor-pointer"
                           style={{ color: "var(--muted)" }}
                           data-testid="lifestyle-include-review-toggle">
                        <input type="checkbox" checked={includeReview}
                               onChange={e => setIncludeReview(e.target.checked)}
                               data-testid="lifestyle-include-review-checkbox" />
                        Include unreviewed
                    </label>
                </div>

                {/* Chip row: FY chips + rolling presets + all time */}
                <div className="flex gap-2 flex-wrap items-center">
                    {fyChips.map(fy => {
                        const k = `fy-${fy}`;
                        const active = activeChip === k;
                        return (
                            <button key={k} onClick={() => setPreset(k)}
                                    data-testid={`lifestyle-chip-${k}`}
                                    className={`px-3 py-1.5 rounded-full text-xs border ${
                                        active
                                            ? "bg-[var(--eucalyptus)] text-[var(--bone)] border-[var(--eucalyptus)]"
                                            : "bg-white text-[var(--ink)] border-[var(--line)] hover:border-[var(--eucalyptus)]"
                                    }`}>
                                {fyLabel(fy)}
                            </button>
                        );
                    })}
                    <div className="w-px h-5 bg-[var(--line)] mx-1" />
                    {[
                        { k: "current", label: "This month" },
                        { k: "3mo", label: "Last 3M" },
                        { k: "6mo", label: "Last 6M" },
                        { k: "12mo", label: "Last 12M" },
                        { k: "all", label: "All time" },
                    ].map(p => {
                        const active = activeChip === p.k;
                        return (
                            <button key={p.k} onClick={() => setPreset(p.k)}
                                    data-testid={`lifestyle-chip-${p.k}`}
                                    className={`px-3 py-1.5 rounded-full text-xs border ${
                                        active
                                            ? "bg-[var(--eucalyptus)] text-[var(--bone)] border-[var(--eucalyptus)]"
                                            : "bg-white text-[var(--ink)] border-[var(--line)] hover:border-[var(--eucalyptus)]"
                                    }`}>
                                {p.label}
                            </button>
                        );
                    })}
                    <button onClick={() => setPickerOpen(o => !o)}
                            data-testid="lifestyle-chip-custom"
                            className={`px-3 py-1.5 rounded-full text-xs border ${
                                activeChip === "custom" || pickerOpen
                                    ? "bg-[var(--eucalyptus)] text-[var(--bone)] border-[var(--eucalyptus)]"
                                    : "bg-white text-[var(--ink)] border-[var(--line)] hover:border-[var(--eucalyptus)]"
                            }`}>
                        {pickerOpen ? "Close picker" : "Pick months…"}
                    </button>
                </div>

                {/* Month grid — click one for single month, click a second to extend to range */}
                {pickerOpen && (
                    <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--line)" }}
                         data-testid="lifestyle-month-picker">
                        <div className="flex justify-between items-center mb-2">
                            <div className="label-eyebrow">
                                {pickerAnchor
                                    ? `Click a second month to extend, or another to start a new range`
                                    : `Click a month for that single month, then click another to extend`}
                            </div>
                            {pickerAnchor && (
                                <button onClick={() => setPickerAnchor(null)}
                                        className="text-xs underline"
                                        style={{ color: "var(--muted)" }}
                                        data-testid="lifestyle-picker-clear-anchor">
                                    Clear
                                </button>
                            )}
                        </div>
                        <div className="grid grid-cols-6 md:grid-cols-12 gap-1">
                            {(available.months || []).map(ym => {
                                const selected = inSelectedRange(ym);
                                const isAnchor = ym === pickerAnchor;
                                return (
                                    <button key={ym} onClick={() => pickMonth(ym)}
                                            data-testid={`lifestyle-picker-${ym}`}
                                            title={available.labels[ym] || ym}
                                            className={`px-1 py-1.5 text-[10px] rounded border ${
                                                isAnchor
                                                    ? "bg-[var(--gold)] text-[var(--ink)] border-[var(--gold)]"
                                                    : selected
                                                        ? "bg-[var(--eucalyptus)] text-[var(--bone)] border-[var(--eucalyptus)]"
                                                        : "bg-white text-[var(--ink)] border-[var(--line)] hover:border-[var(--eucalyptus)]"
                                            }`}>
                                        {(available.labels[ym] || ym).replace(" ", "\u00a0")}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-10">
                <div className="hairline bg-white p-6 rounded" data-testid="lifestyle-annual-budget">
                    <div className="label-eyebrow">Annual Budget</div>
                    <div className="font-serif text-3xl mt-3 font-mono-num">{AUD(totalBudget)}</div>
                </div>
                <div className="hairline bg-white p-6 rounded" data-testid="lifestyle-actual">
                    <div className="label-eyebrow">
                        Actual · {VIEW_OPTIONS.find(v => v.key === view)?.label}
                        {range.active && ` · ${available.labels[range.from]} → ${available.labels[range.to]}`}
                    </div>
                    <div className="font-serif text-3xl mt-3 font-mono-num">{AUD(totalActual)}</div>
                </div>
                <div className="p-6 rounded" style={{ background: "var(--eucalyptus)", color: "var(--bone)" }}
                     data-testid="lifestyle-vs-budget">
                    <div className="label-eyebrow" style={{ color: "rgba(255,255,255,0.75)" }}>vs Budget</div>
                    <div className="font-serif text-3xl mt-3 font-mono-num">{PCT(totalActual / (totalBudget || 1), 0)}</div>
                </div>
            </div>

            <div className="hairline bg-white rounded overflow-hidden">
                <div className="flex justify-end p-3 border-b" style={{ borderColor: "var(--line)" }}>
                    <label className="text-xs flex items-center gap-2 cursor-pointer"
                           style={{ color: "var(--muted)" }}
                           data-testid="lifestyle-show-workbook-toggle">
                        <input type="checkbox" checked={showWorkbook}
                               onChange={e => setShowWorkbook(e.target.checked)}
                               data-testid="lifestyle-show-workbook-checkbox" />
                        Show baseline column
                    </label>
                </div>
                <table className="editorial w-full" data-testid="lifestyle-table">
                    <thead>
                        <tr>
                            <th>Category</th>
                            <th className="num">Monthly Budget</th>
                            <th className="num">Annual Budget</th>
                            {showWorkbook && <th className="num">Baseline</th>}
                            <th className="num">Transactions</th>
                            <th className="num">Actual ({view})</th>
                            <th className="num">% of Budget</th>
                            <th>Status</th>
                            <th style={{ width: 80 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(c => {
                            const ratio = c._actual / (c.annual_budget || 1);
                            const isEditing = editing?.id === c.id;
                            return (
                                <tr key={c.id} data-testid={`lifestyle-row-${c.id}`}>
                                    <td className="font-medium">{c.category}</td>
                                    <td className="num font-mono-num">
                                        {isEditing ? (
                                            <input type="number" step="any"
                                                   value={editing.monthly}
                                                   onChange={e => onMonthlyChange(e.target.value)}
                                                   data-testid={`lifestyle-edit-monthly-${c.id}`}
                                                   className="border border-[var(--line)] px-2 py-1 rounded text-sm w-28 text-right font-mono-num" />
                                        ) : (
                                            <span className="text-[var(--muted)]">{AUD(c.monthly_budget)}</span>
                                        )}
                                    </td>
                                    <td className="num font-mono-num">
                                        {isEditing ? (
                                            <input type="number" step="any"
                                                   value={editing.annual}
                                                   onChange={e => onAnnualChange(e.target.value)}
                                                   data-testid={`lifestyle-edit-annual-${c.id}`}
                                                   className="border border-[var(--line)] px-2 py-1 rounded text-sm w-32 text-right font-mono-num" />
                                        ) : (
                                            AUD(c.annual_budget)
                                        )}
                                    </td>
                                    {showWorkbook && (
                                        <td className="num font-mono-num text-[var(--muted)]">{AUD(c.actual_workbook || 0)}</td>
                                    )}
                                    <td className="num font-mono-num text-[var(--muted)]">{AUD(c.actual_from_transactions || 0)}</td>
                                    <td className="num font-mono-num font-medium">{AUD(c._actual)}</td>
                                    <td className="num font-mono-num">
                                        <div className="inline-flex items-center gap-3 justify-end">
                                            <div className="w-24 h-1.5 bg-[var(--line)] rounded overflow-hidden">
                                                <div className="h-full"
                                                     style={{ width: `${Math.min(100, ratio * 100)}%`,
                                                              background: ratio > 1.2 ? "var(--terracotta)" : ratio > 1 ? "var(--gold)" : "var(--eucalyptus)" }} />
                                            </div>
                                            <span className="w-14 text-right">{PCT(ratio, 0)}</span>
                                        </div>
                                    </td>
                                    <td><span className={`chip ${chip(c._status)}`}>{c._status}</span></td>
                                    <td>
                                        {isEditing ? (
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => saveEdit(c)} disabled={busy}
                                                        className="text-[var(--eucalyptus)] hover:opacity-70"
                                                        data-testid={`lifestyle-save-${c.id}`}>
                                                    <Check size={16} />
                                                </button>
                                                <button onClick={cancelEdit}
                                                        className="text-[var(--muted)] hover:text-[var(--terracotta)]">
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        ) : (
                                            <button onClick={() => startEdit(c)}
                                                    className="text-[var(--muted)] hover:text-[var(--ink)]"
                                                    data-testid={`lifestyle-edit-${c.id}`}>
                                                <PencilSimple size={14} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
